
import axios from 'axios';

const AVATAR_ID_REGEX = /\/api\/show-avatar\/([\w-]+)\.png/;

const liveAvatarUtil = {
  readyCache: {},
  generating: new Set(),
  activePolls: {},

  init(el) {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          const avatars = node.matches('.avatar') ? [node] : [...node.querySelectorAll('.avatar')];
          for (const avatar of avatars) {
            const match = avatar.style.backgroundImage.match(AVATAR_ID_REGEX);
            if (match) this.processAvatar(avatar, match[1]);
          }
        }
        for (const node of mutation.removedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          const avatars = node.matches('.avatar') ? [node] : [...node.querySelectorAll('.avatar')];
          for (const avatar of avatars) {
            const match = avatar.style.backgroundImage.match(AVATAR_ID_REGEX);
            if (match) this.cancelPoll(match[1]);
          }
        }
      }
    });
    observer.observe(el, { childList: true, subtree: true });
  },

  cancelPoll(avatarId) {
    if (this.activePolls[avatarId]) {
      console.log(`[live-avatar] ${avatarId} removed from DOM, cancelling poll`);
      clearTimeout(this.activePolls[avatarId]);
      delete this.activePolls[avatarId];
    }
  },

  async processAvatar(avatarDiv, avatarId) {
    console.log(`[live-avatar] found avatar ${avatarId}`);
    if (this.readyCache[avatarId]) {
      console.log(`[live-avatar] ${avatarId} cached, replacing with video`);
      this.replaceWithVideo(avatarDiv, avatarId);
      return;
    }

    try {
      const res = await axios.get(`/api/avatar-video/${avatarId}`);
      console.log(`[live-avatar] ${avatarId} status: ${res.data.status}`);
      if (res.data.status === 'ready') {
        this.readyCache[avatarId] = true;
        if (avatarDiv.isConnected) this.replaceWithVideo(avatarDiv, avatarId);
        return;
      }
      if (res.data.status !== 'ready' && !this.generating.has(avatarId)) {
        console.log(`[live-avatar] ${avatarId} requesting generation (was: ${res.data.status})`);
        this.generating.add(avatarId);
        axios.post(`/api/avatar-video/${avatarId}/generate`).catch(() => {});
      }
      this.pollUntilReady(avatarDiv, avatarId);
    } catch (e) {
      console.log('[live-avatar] check failed:', e);
    }
  },

  pollUntilReady(avatarDiv, avatarId) {
    this.cancelPoll(avatarId);

    const poll = async () => {
      if (!avatarDiv.isConnected) {
        delete this.activePolls[avatarId];
        return;
      }
      const bgMatch = avatarDiv.style.backgroundImage.match(AVATAR_ID_REGEX);
      if (!bgMatch || bgMatch[1] !== avatarId) {
        delete this.activePolls[avatarId];
        return;
      }

      try {
        const res = await axios.get(`/api/avatar-video/${avatarId}`);
        if (res.data.status === 'ready') {
          console.log(`[live-avatar] ${avatarId} ready, replacing with video`);
          this.readyCache[avatarId] = true;
          if (avatarDiv.isConnected) this.replaceWithVideo(avatarDiv, avatarId);
          delete this.activePolls[avatarId];
          return;
        }
        if (res.data.status === 'failed') {
          console.log(`[live-avatar] ${avatarId} failed, retrying generation`);
          this.generating.delete(avatarId);
          axios.post(`/api/avatar-video/${avatarId}/generate`).catch(() => {});
          // keep polling — generate will reset status to 'generating'
        }
      } catch (e) { /* keep polling */ }

      this.activePolls[avatarId] = setTimeout(poll, 1000);
    };

    this.activePolls[avatarId] = setTimeout(poll, 1000);
  },

  replaceWithVideo(avatarDiv, avatarId) {
    avatarDiv.style.backgroundImage = 'none';
    avatarDiv.innerHTML = '';
    const video = document.createElement('video');
    video.src = `/api/show-avatar-video/${avatarId}.mp4`;
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.disableRemotePlayback = true;
    video.style.cssText = 'width:100%; height:100%; object-fit:cover; object-position:center 25%;';
    avatarDiv.appendChild(video);
  },
};

export default liveAvatarUtil;
